import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { DroneRecordDto, type DroneRecord, type SampleMeta } from '@tonist/shared';
import { PageShell } from '../components/PageShell';
import { FamilyDot } from '../components/FamilyDot';
import { sourcesSummary } from '../components/DroneCard';
import { get, post, errorMessage } from '../lib/api';
import { useDrones } from '../store/dronesStore';
import { useSettings } from '../store/settingsStore';
import { useAuth } from '../store/authStore';
import { toast } from '../store/uiStore';

interface PublicPage { record: DroneRecord; samples: SampleMeta[] }

/** Accept `{ item }`, `{ record }`, `{ drone: <record> }` or a bare DroneRecord, plus optional `samples`. */
function parsePublic(data: unknown): PublicPage {
  const o = (data ?? {}) as Record<string, unknown>;
  for (const cand of [o.item, o.record, o.drone, o]) {
    const p = DroneRecordDto.safeParse(cand);
    if (p.success) return { record: p.data, samples: Array.isArray(o.samples) ? (o.samples as SampleMeta[]) : [] };
  }
  throw new Error('Unexpected response for a public drone');
}

export function PublicDrone() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const [page, setPage] = useState<PublicPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    get<unknown>(`/d/${encodeURIComponent(slug)}`).then((d) => { if (alive) setPage(parsePublic(d)); }).catch((e: unknown) => { if (alive) setError(errorMessage(e)); });
    return () => { alive = false; };
  }, [slug]);

  const play = () => {
    if (!page) return;
    useDrones.getState().stashTransient(page.record);
    useSettings.getState().setDroneIds([page.record.id]);
    void post(`/d/${encodeURIComponent(slug)}/play`).catch(() => undefined);
    navigate('/');
  };
  const clone = async () => {
    if (!page) return;
    setBusy(true);
    try { const rec = await useDrones.getState().clone(page.record); toast('Cloned to my drones'); navigate(`/drones/${rec.id}`); }
    catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };

  if (error) return <PageShell title="Drone not found"><div className="notice notice--err">{error}</div><Link to="/gallery" className="btn">Browse the gallery</Link></PageShell>;
  if (!page) return <PageShell title="…"><div className="hint">Loading</div></PageShell>;
  const { record: r, samples } = page;
  const d = r.drone;
  const credits = samples.filter((s) => s.credit || s.license);
  return (
    <PageShell eyebrow={`Shared drone · ${r.plays} plays`} title={d.name}>
      <div className="two-col">
        <div className="glass panel stack sticky">
          <div className="row"><FamilyDot family={d.family} /><span className="hint">by {r.authorName ?? 'anonymous'}</span></div>
          <button type="button" className="btn btn--primary btn--lg" onClick={play}>Play on the mandala</button>
          {user
            ? <button type="button" className="btn" disabled={busy} onClick={() => void clone()}>Clone to my drones</button>
            : <div className="hint"><Link to="/auth">Sign in</Link> to clone this drone into your own — or <button type="button" className="btn btn--sm" onClick={() => void clone()}>keep a local copy</button>.</div>}
        </div>
        <div className="glass panel stack prose">
          <p>{d.description || 'No description.'}</p>
          <div className="hint">Sources: {sourcesSummary(d)}</div>
          {credits.length > 0 && (
            <div className="stack">
              <div className="eyebrow">Sample credits</div>
              {credits.map((s) => <div key={s.id} className="hint">{s.name}: {s.credit}{s.license ? ` · ${s.license}` : ''}{s.sourceUrl ? <> · <a href={s.sourceUrl}>source</a></> : null}</div>)}
            </div>
          )}
          {r.forkedFrom && <div className="hint">Cloned from another drone.</div>}
        </div>
      </div>
    </PageShell>
  );
}
